import type {
  CashFlowBadgeTone,
  CashFlowStepStatus,
  CashFlowTimelineBranch,
  CashFlowTimelineStep,
} from '@kioskgaming/ui';
import { formatInTimezone, getClientTimezone } from '@kioskgaming/ui';
import type { Transaction, TransactionAllocation, TransactionOwnerParty } from '../types';
import { getBalanceToCreditsConversionDisplay } from './balanceToCreditsConversionDisplay';
import { formatCurrency } from './index';
import { buildFeeBreakdownMetaKvs, buildSingleFeeMetaKv } from './feeBreakdownMeta';
import type { AllocationFulfillmentSummary } from './transactionAllocationSummary';
import {
  buildGameDepositAllocationCashFlowSteps,
  shouldBuildAllocationCashFlow,
} from './transactionAllocationCashFlow';
import {
  buildGameWithdrawalCashFlowSteps,
  shouldBuildGameWithdrawalCashFlow,
} from './transactionGameWithdrawalCashFlow';
import {
  getWithdrawalPayoutRefundInfo,
  type WithdrawalPayoutRefundDisplay,
} from './withdrawalPayoutRefundDisplay';

type WalletLeg = NonNullable<Transaction['relatedWalletTransactions']>[number];
type UsdLeg = NonNullable<Transaction['relatedUsdWalletTransactions']>[number];

function mapStepStatus(status: string | undefined): CashFlowStepStatus | undefined {
  if (!status) return undefined;
  const s = status.toLowerCase();
  if (s === 'completed' || s === 'approved' || s === 'success') return 'success';
  if (s === 'failed' || s === 'rejected' || s === 'cancelled') return 'failed';
  if (s === 'pending' || s === 'expired') return 'pending';
  return status;
}

function partyFields(party: TransactionOwnerParty | null | undefined) {
  if (!party) {
    return { ownerType: undefined, ownerName: undefined, subtitle: undefined };
  }
  const name =
    party.name?.trim() ||
    party.displayLabel?.split('·').pop()?.trim() ||
    undefined;
  return {
    ownerType: party.roleLabel || party.type,
    ownerName: name,
    subtitle: party.displayLabel || undefined,
  };
}

function walletNameFromParty(party: TransactionOwnerParty | null | undefined, fallback: string): string {
  if (!party) return fallback;
  const t = String(party.type || '').toLowerCase();
  // Distinguish: credit ledger (main wallet) vs USD wallet.
  if (t === 'player' || t === 'user') return 'Player credit Wallet';
  if (party?.roleLabel) return `${party.roleLabel} Wallet`;
  if (party?.type) return `${party.type} Wallet`;
  return fallback;
}

function detectBranchBadge(party: TransactionOwnerParty | null | undefined): CashFlowBadgeTone | undefined {
  const haystack = `${party?.type || ''} ${party?.roleLabel || ''} ${party?.displayLabel || ''}`.toLowerCase();
  if (haystack.includes('fee')) return 'fee';
  if (haystack.includes('platform') || haystack.includes('system')) return 'system';
  return undefined;
}

function formatCreditsAmount(amount: number | string, isOut: boolean): string {
  const n = Math.abs(Number(amount || 0));
  const formatted = n.toLocaleString('en-US');
  return isOut ? `-${formatted} credits` : `+${formatted} credits`;
}

function formatCreditsBalance(amount: number | string | undefined | null): string | undefined {
  if (amount == null) return undefined;
  const n = Number(amount);
  if (!Number.isFinite(n)) return undefined;
  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(n);
  return `${formatted} credits`;
}

function formatUsdSigned(amount: number, currency: string, isOut: boolean): string {
  const formatted = formatCurrency(amount, currency);
  return isOut ? `-${formatted}` : `+${formatted}`;
}

function formatTimestamp(iso?: string, timezone = getClientTimezone()): string | undefined {
  if (!iso) return undefined;
  const result = formatInTimezone(iso, timezone);
  return result === '—' ? undefined : result;
}

function legConnectorLabel(leg: WalletLeg, fallback: string): string {
  const flow = (leg.flowLabel || '').trim();
  if (flow) return flow;
  const desc = (leg.description || '').trim();
  if (desc && desc.length <= 80) return desc;
  return fallback;
}

function clearLastConnector(steps: CashFlowTimelineStep[]) {
  if (steps.length > 0) {
    const last = steps[steps.length - 1];
    delete last.connectorAfter;
  }
}

function legToOutStep(leg: WalletLeg, options: { variant?: 'source' | 'default'; connectorAfter?: string }, tz: string): CashFlowTimelineStep {
  const walletName = walletNameFromParty(leg.fromParty, 'Wallet');
  const before = formatCreditsBalance(leg.balanceBefore);
  const after = formatCreditsBalance(leg.balanceAfter);
  return {
    id: leg.id,
    variant: options.variant,
    walletName,
    ...partyFields(leg.fromParty),
    direction: 'out',
    amount: formatCreditsAmount(leg.amount, true),
    status: mapStepStatus(leg.status),
    hideStatusInDetails: walletName === 'Player credit Wallet',
    actionLabel: leg.legSummary || leg.description || undefined,
    timestamp: formatTimestamp(leg.created_at, tz),
    balance: before && after ? { before, after } : undefined,
    connectorAfter: options.connectorAfter ? { label: options.connectorAfter } : undefined,
  };
}

function legToInStep(leg: WalletLeg, tz: string): CashFlowTimelineStep {
  const walletName = walletNameFromParty(leg.toParty, 'Wallet');
  const before = formatCreditsBalance(leg.balanceBefore);
  const after = formatCreditsBalance(leg.balanceAfter);
  return {
    id: leg.id,
    walletName,
    ...partyFields(leg.toParty),
    direction: 'in',
    amount: formatCreditsAmount(leg.amount, false),
    status: mapStepStatus(leg.status),
    hideStatusInDetails: walletName === 'Player credit Wallet',
    actionLabel: leg.description || leg.legSummary || undefined,
    timestamp: formatTimestamp(leg.created_at, tz),
    balance: before && after ? { before, after } : undefined,
  };
}

function legToBranch(leg: WalletLeg): CashFlowTimelineBranch {
  const badge = detectBranchBadge(leg.toParty);
  return {
    id: leg.id,
    walletName: walletNameFromParty(leg.toParty, 'Wallet'),
    ...partyFields(leg.toParty),
    direction: 'in',
    amount: formatCreditsAmount(leg.amount, false),
    badge: badge ?? 'in',
    status: mapStepStatus(leg.status),
    actionLabel: leg.description || leg.legSummary || undefined,
  };
}

function buildFromWalletLegs(legs: WalletLeg[], tz: string): CashFlowTimelineStep[] {
  const debits = legs.filter((l) => String(l.type).toLowerCase() === 'debit');
  const credits = legs.filter((l) => String(l.type).toLowerCase() !== 'debit');
  const steps: CashFlowTimelineStep[] = [];

  debits.forEach((leg, index) => {
    const hasMore = index < debits.length - 1 || credits.length > 0;
    const nextConnector =
      credits.length > 1 && index === debits.length - 1
        ? 'Settlement split'
        : credits[0]
          ? legConnectorLabel(credits[0], 'Transfer')
          : debits[index + 1]
            ? legConnectorLabel(debits[index + 1], 'Next step')
            : undefined;

    steps.push(
      legToOutStep(leg, {
        variant: index === 0 ? 'source' : 'default',
        connectorAfter: hasMore && nextConnector ? nextConnector : undefined,
      }, tz),
    );
  });

  if (credits.length === 1) {
    steps.push(legToInStep(credits[0], tz));
  } else if (credits.length > 1) {
    const totalOut = debits.reduce((sum, leg) => sum + Math.abs(Number(leg.amount || 0)), 0);
    const totalIn = credits.reduce((sum, leg) => sum + Math.abs(Number(leg.amount || 0)), 0);
    const balanced = totalOut > 0 && Math.abs(totalOut - totalIn) < 0.01;

    steps.push({
      id: 'cash-flow-split',
      variant: 'split',
      walletName: 'Split destinations',
      direction: 'out',
      amount: totalOut > 0 ? formatCreditsAmount(totalOut, true) : formatCreditsAmount(totalIn, true),
      splitDescription: `Funds split across ${credits.length} wallets.`,
      balancedLabel: balanced
        ? `${totalIn.toLocaleString('en-US')} credits balanced`
        : undefined,
      status: balanced ? 'success' : 'pending',
      branches: credits.map(legToBranch),
    });
  }

  clearLastConnector(steps);
  return steps;
}

function buildFromUsdWalletLegs(legs: UsdLeg[], tz: string): CashFlowTimelineStep[] {
  if (!legs.length) return [];
  const sorted = [...legs].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  return sorted.map((leg, index) => {
    const t = String(leg.type || '').toLowerCase();
    const isOut = t === 'withdrawal' || t === 'purchase_credits';
    const grossAmount = Math.abs(Number(leg.amountGross ?? 0));
    const netAmount = Math.abs(Number(leg.amountNet ?? leg.amountGross ?? 0));
    const feeAmount = Math.max(0, grossAmount - netAmount);
    const before = leg.balanceNetBefore != null ? formatCurrency(Number(leg.balanceNetBefore), 'USD') : undefined;
    const after = leg.balanceNetAfter != null ? formatCurrency(Number(leg.balanceNetAfter), 'USD') : undefined;
    const ownerLabel = leg.ownerParty?.displayLabel || leg.ownerParty?.roleLabel || 'USD owner';
    const phone = leg.ownerContact?.phone ? String(leg.ownerContact.phone).trim() : '';
    const email = leg.ownerContact?.email ? String(leg.ownerContact.email).trim() : '';
    const contactBits = [phone, email].filter(Boolean);
    const contactSuffix = contactBits.length ? ` - ${contactBits.join(' · ')}` : '';
    const subtitleHref = leg.ownerParty?.refLink || undefined;
    const isLast = index === sorted.length - 1;
    const connectorLabel = !isLast
      ? sorted[index + 1]?.flowLabel || sorted[index + 1]?.legSummary || 'USD wallet movement'
      : undefined;

    const metaKvs: Array<{ label: string; value: string }> = [];
    const feeLines = buildFeeBreakdownMetaKvs(leg.feeBreakdown ?? undefined);
    const shouldShowFee =
      grossAmount > 0 &&
      (Math.abs(grossAmount - netAmount) > 0.0001 || feeLines.length > 0);
    if (shouldShowFee) {
      metaKvs.push({ label: 'Gross', value: formatCurrency(grossAmount, 'USD') });
      if (feeLines.length > 0) {
        metaKvs.push(...feeLines);
      } else if (feeAmount > 0.0001) {
        metaKvs.push(buildSingleFeeMetaKv(grossAmount, feeAmount));
      }
    }

    return {
      id: `usd-${leg.id}`,
      variant: index === 0 ? 'source' : 'default',
      walletName: 'USD Wallet',
      subtitle: `${ownerLabel}${contactSuffix}`,
      subtitleHref,
      direction: isOut ? 'out' : 'in',
      amount: formatUsdSigned(netAmount, 'USD', isOut),
      status: undefined,
      actionLabel: leg.legSummary || leg.flowLabel || undefined,
      balance: before || after ? { before, after } : undefined,
      timestamp: formatTimestamp(leg.created_at, tz),
      metaKvs: metaKvs.length ? metaKvs : undefined,
      connectorAfter: connectorLabel ? { label: connectorLabel } : undefined,
    };
  });
}

function buildFromParties(transaction: Transaction, tz: string): CashFlowTimelineStep[] {
  const steps: CashFlowTimelineStep[] = [];
  const fs = transaction.fundingSummary;
  const currency = transaction.currency || 'USD';
  const amountAbs = Math.abs(Number(transaction.amount || 0));
  const txStatus = mapStepStatus(transaction.status);

  if (transaction.depositor) {
    const outAmount =
      fs?.creditAmount != null
        ? `-${Number(fs.creditAmount).toLocaleString('en-US')} credits`
        : formatUsdSigned(amountAbs, currency, true);

    steps.push({
      id: 'depositor',
      variant: 'source',
      walletName: walletNameFromParty(transaction.depositor, 'Source wallet'),
      ...partyFields(transaction.depositor),
      direction: 'out',
      amount: outAmount,
      status: txStatus,
      actionLabel:
        fs?.fundingSource != null
          ? `Funded via ${fs.fundingSource}`
          : transaction.type === 'deposit'
            ? 'Payment received'
            : 'Withdrawal sent',
      timestamp: formatTimestamp(transaction.created_at, tz),
      connectorAfter: transaction.recipient
        ? {
            label: transaction.recipient.displayLabel
              ? `To ${transaction.recipient.displayLabel}`
              : 'Transfer to recipient',
          }
        : undefined,
    });
  }

  if (transaction.recipient) {
    const inAmount =
      fs?.creditAmount != null
        ? `+${Number(fs.creditAmount).toLocaleString('en-US')} credits`
        : formatUsdSigned(amountAbs, currency, false);

    steps.push({
      id: 'recipient',
      walletName: walletNameFromParty(transaction.recipient, 'Recipient wallet'),
      ...partyFields(transaction.recipient),
      direction: 'in',
      amount: inAmount,
      status: txStatus,
      actionLabel: transaction.type === 'deposit' ? 'Credits received' : 'Payout received',
      timestamp: formatTimestamp(transaction.updated_at || transaction.created_at, tz),
    });
  }

  if (steps.length === 1 && fs?.cashCharged != null && transaction.type === 'deposit') {
    steps[0].connectorAfter = {
      label: `Cash charged ${formatCurrency(Number(fs.cashCharged), currency)}`,
    };
  }

  clearLastConnector(steps);
  return steps;
}

export type BuildTransactionCashFlowTimelineOptions = {
  allocations?: TransactionAllocation[];
  allocationSummary?: AllocationFulfillmentSummary | null;
};

function payoutRefundFailureConnector(transaction: Transaction): string {
  const provider = transaction.provider ? String(transaction.provider) : 'gateway';
  const err = (transaction.errorMessage || '').trim();
  if (err) return `External payout via ${provider} failed — ${err}`;
  return `External payout via ${provider} failed — no funds sent`;
}

function payoutRefundMetaKvs(
  transaction: Transaction,
  refund: WithdrawalPayoutRefundDisplay,
): Array<{ label: string; value: string }> {
  const meta: Array<{ label: string; value: string }> = [];
  if (transaction.provider) meta.push({ label: 'Gateway', value: String(transaction.provider) });
  if (transaction.paymentMethod) meta.push({ label: 'Payment method', value: String(transaction.paymentMethod) });
  if (transaction.externalTransactionId) {
    meta.push({ label: 'External ref', value: String(transaction.externalTransactionId) });
  }
  meta.push({ label: 'Wallet credit ID', value: refund.refundTransactionId });
  if (refund.walletTransactionId != null) {
    meta.push({ label: 'Ledger row', value: `#${refund.walletTransactionId}` });
  }
  return meta;
}

function buildSyntheticPayoutRefundCashFlowSteps(
  transaction: Transaction,
  refund: WithdrawalPayoutRefundDisplay,
  timezone: string,
): CashFlowTimelineStep[] {
  const currency = transaction.currency || 'USD';
  const amount = Math.abs(Number(transaction.amount || refund.refundAmount));
  const failureConnector = payoutRefundFailureConnector(transaction);

  return [
    {
      id: 'payout-refund-debit',
      variant: 'source',
      walletName: 'Player credit Wallet',
      direction: 'out',
      amount: formatUsdSigned(amount, currency, true),
      status: 'success',
      actionLabel: 'Debited for withdrawal',
      hideStatusInDetails: true,
      timestamp: formatTimestamp(transaction.approvedAt || transaction.created_at, timezone),
      connectorAfter: { label: failureConnector },
    },
    {
      id: 'payout-refund-credit',
      walletName: 'Player credit Wallet',
      direction: 'in',
      amount: formatUsdSigned(refund.refundAmount, currency, false),
      status: 'success',
      actionLabel: 'Refunded after payout failure',
      hideStatusInDetails: true,
      timestamp: formatTimestamp(refund.refundedAt, timezone),
      metaKvs: payoutRefundMetaKvs(transaction, refund),
    },
  ];
}

function applyPayoutRefundCashFlowOverlay(
  steps: CashFlowTimelineStep[],
  transaction: Transaction,
  timezone: string,
): CashFlowTimelineStep[] {
  const refund = getWithdrawalPayoutRefundInfo(transaction);
  if (!refund) return steps;

  const failureConnector = payoutRefundFailureConnector(transaction);
  const outSteps = steps.filter((s) => s.direction === 'out');
  const inSteps = steps.filter((s) => s.direction === 'in');

  if (outSteps.length === 0 && inSteps.length === 0) {
    return buildSyntheticPayoutRefundCashFlowSteps(transaction, refund, timezone);
  }

  let relabeledOut = false;
  let relabeledIn = false;
  const enhanced = steps.map((step) => {
    if (step.direction === 'out' && !relabeledOut) {
      relabeledOut = true;
      return {
        ...step,
        status: 'success' as CashFlowStepStatus,
        actionLabel: 'Debited for withdrawal',
        connectorAfter: { label: failureConnector },
      };
    }
    if (step.direction === 'in' && !relabeledIn) {
      relabeledIn = true;
      return {
        ...step,
        status: 'success' as CashFlowStepStatus,
        actionLabel: 'Refunded after payout failure',
        timestamp: formatTimestamp(refund.refundedAt, timezone) || step.timestamp,
        metaKvs: [
          ...(step.metaKvs || []),
          { label: 'Wallet credit ID', value: refund.refundTransactionId },
        ],
      };
    }
    return step;
  });

  if (relabeledOut && relabeledIn) {
    clearLastConnector(enhanced);
    return enhanced;
  }

  if (relabeledOut && !relabeledIn) {
    clearLastConnector(enhanced);
    const currency = transaction.currency || 'USD';
    return [
      ...enhanced,
      {
        id: `payout-refund-credit-${refund.refundTransactionId}`,
        walletName: 'Player credit Wallet',
        direction: 'in',
        amount: formatUsdSigned(refund.refundAmount, currency, false),
        status: 'success',
        actionLabel: 'Refunded after payout failure',
        hideStatusInDetails: true,
        timestamp: formatTimestamp(refund.refundedAt, timezone),
        metaKvs: payoutRefundMetaKvs(transaction, refund),
      },
    ];
  }

  // Misleading single IN leg (e.g. parties fallback "Payout received" + failed).
  return buildSyntheticPayoutRefundCashFlowSteps(transaction, refund, timezone);
}

/** Build timeline steps from admin transaction detail payload. */
export function buildTransactionCashFlowTimeline(
  transaction: Transaction,
  timezone = getClientTimezone(),
  options?: BuildTransactionCashFlowTimelineOptions,
): CashFlowTimelineStep[] {
  const legs = transaction.relatedWalletTransactions;
  const usdLegs = transaction.relatedUsdWalletTransactions;
  let built: CashFlowTimelineStep[];
  if (legs && legs.length > 0 && usdLegs && usdLegs.length > 0) {
    const creditSteps = buildFromWalletLegs(legs, timezone);
    const usdSteps = buildFromUsdWalletLegs(usdLegs, timezone);
    if (usdSteps.length > 0 && creditSteps.length > 0) {
      usdSteps[0].variant = 'default';
    }
    built = [...creditSteps, ...usdSteps];
  } else if (usdLegs && usdLegs.length > 0) {
    built = buildFromUsdWalletLegs(usdLegs, timezone);
  } else if (legs && legs.length > 0) {
    built = buildFromWalletLegs(legs, timezone);
  } else {
    built = buildFromParties(transaction, timezone);
  }

  if (
    options?.allocations?.length &&
    options.allocationSummary &&
    shouldBuildAllocationCashFlow(transaction, options.allocations)
  ) {
    built = buildGameDepositAllocationCashFlowSteps(
      transaction,
      options.allocations,
      options.allocationSummary,
      timezone,
    );
  } else if (
    shouldBuildGameWithdrawalCashFlow(transaction) &&
    (transaction.relatedWalletTransactions?.length ?? 0) === 0
  ) {
    built = buildGameWithdrawalCashFlowSteps(transaction, timezone);
  }

  const metaKvs: Array<{ label: string; value: string }> = [];
  if (transaction.provider) metaKvs.push({ label: 'Gateway', value: String(transaction.provider) });
  if (transaction.paymentMethod) metaKvs.push({ label: 'Payment method', value: String(transaction.paymentMethod) });
  if (transaction.externalTransactionId) metaKvs.push({ label: 'External ref', value: String(transaction.externalTransactionId) });

  if (metaKvs.length) {
    built = built.map((s) =>
      s.walletName === 'Player credit Wallet' || s.walletName === 'Player master wallet' ? { ...s, metaKvs } : s,
    );
  }

  const playerId = transaction.user?.id || transaction.userId || '';
  const username = transaction.user?.username ? String(transaction.user.username).trim() : '';
  const phone = transaction.user?.phone ? String(transaction.user.phone).trim() : '';
  const contactSuffix = phone ? ` - ${phone}` : '';
  if (playerId) {
    built = built.map((s) => {
      if (s.walletName !== 'Player credit Wallet' && s.walletName !== 'Player master wallet') return s;
      const fallbackBase = s.subtitle || s.ownerName || `Player · ${playerId}`;
      const base = username || fallbackBase;
      const shouldAppendPhone = phone && !String(base).includes(phone);
      return {
        ...s,
        // Single readable line; avoid duplicate Owner line in UI.
        subtitle: `${base}${shouldAppendPhone ? contactSuffix : ''}`,
        subtitleHref: `/users/${encodeURIComponent(String(playerId))}`,
        ownerType: undefined,
        ownerName: undefined,
      };
    });
  }

  built = built.map((s) =>
    s.walletName === 'Player credit Wallet' || s.walletName === 'Player master wallet'
      ? { ...s, hideStatusInDetails: true }
      : s,
  );

  const conversion = getBalanceToCreditsConversionDisplay(transaction);
  if (conversion) {
    const conversionMeta: Array<{ label: string; value: string }> = [
      { label: `${conversion.buyerRoleLabel} cost rate`, value: conversion.costRatePercentLabel },
      { label: 'Conversion', value: conversion.formulaLine },
    ];
    let applied = false;
    built = built.map((s) => {
      if (applied || s.direction !== 'in' || !String(s.amount).includes('credit')) return s;
      applied = true;
      return {
        ...s,
        metaKvs: [...(s.metaKvs || []), ...conversionMeta],
      };
    });
    if (!applied && built.length > 0) {
      built[0] = {
        ...built[0],
        metaKvs: [...(built[0].metaKvs || []), ...conversionMeta],
      };
    }
  }

  built = applyPayoutRefundCashFlowOverlay(built, transaction, timezone);

  return built;
}

export function hasTransactionCashFlowTimeline(transaction: Transaction): boolean {
  return buildTransactionCashFlowTimeline(transaction).length > 0;
}
